"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck, ChevronRight, CreditCard, Globe, Lock, Mail, Send, ShieldCheck, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: "01",
  title: "Register Profile",
  description: "Open your TradeSphere account in seconds. Our high-security portal ensures your data remains encrypted with military-grade protocols.",
  icon: UserPlus,
  demo: "create"
}, {
  id: "02",
  title: "Fund Account",
  description: "Deposit funds instantly using multiple gateways. Our system supports rapid liquidity transfers to ensure you never miss a market move.",
  icon: CreditCard,
  demo: "verify"
}, {
  id: "03",
  title: "Execute Trades",
  description: "Access global markets with sub-millisecond execution. Withdraw your profits anytime with our automated instant-liquidation system.",
  icon: Send,
  demo: "transact"
}];
function StepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3,
    once: false
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="158558" ref={ref} data-index={index} className="relative flex flex-col md:flex-row items-center justify-between gap-8 md:gap-16 w-full mb-24 last:mb-0">
      {}
      <div className="absolute left-6 md:left-1/2 top-0 bottom-[-6rem] md:bottom-[-6rem] w-px -translate-x-1/2 flex flex-col items-center z-10">
        <div className={`w-px h-full transition-all duration-1000 ease-in-out ${isVisible ? "bg-primary shadow-[0_0_15px_var(--color-primary)]" : "bg-border/20"}`} />
        <div className={`absolute top-8 w-4 h-4 rounded-full border-2 transition-all duration-700 ease-out ${isVisible ? "bg-background border-primary shadow-[0_0_20px_var(--color-primary)] scale-125" : "bg-background border-muted-foreground/30 scale-100"}`} />
      </div>

      {}
      <div className={`w-full pl-16 md:pl-0 md:w-1/2 flex flex-col ${isEven ? "md:pr-16 md:items-end md:text-right" : "md:pl-16 md:order-2 md:items-start md:text-left"} transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="relative mb-4">
          <span className="absolute -top-12 -left-4 md:-top-16 md:-left-8 text-7xl md:text-9xl font-black text-transparent select-none pointer-events-none z-0" style={{
          WebkitTextStroke: `2px ${isVisible ? "var(--color-primary)" : "var(--color-muted-foreground)"}`,
          opacity: isVisible ? 0.15 : 0.05,
          transition: "all 0.7s ease-out"
        }}>
            {step.id}
          </span>
          <div className="relative z-10 flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-sm bg-primary/10 text-primary ${isEven ? "md:order-2" : ""}`}>
              <step.icon className="w-6 h-6" />
            </div>
            <h3 className="text-2xl md:text-3xl font-black text-foreground tracking-tight">{step.title}</h3>
          </div>
        </div>
        <p className="text-muted-foreground text-lg leading-relaxed max-w-md">
          {step.description}
        </p>
      </div>

      {}
      <div className={`w-full pl-16 md:pl-0 md:w-1/2 flex justify-center ${isEven ? "md:pl-16 md:justify-start" : "md:pr-16 md:order-1 md:justify-end"} transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="w-full max-w-sm relative group">
          {}
          <div className={`absolute -inset-1 rounded-sm blur-xl transition-opacity duration-1000 ${isVisible ? "opacity-20 bg-primary/40" : "opacity-0"}`} />
          
          <Card className="relative bg-card/80 backdrop-blur-xl border-primary/20 shadow-2xl overflow-hidden rounded-sm">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
            
            {step.demo === "create" && <>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <Mail className="w-4 h-4 text-primary" />
                    Secure Portal
                  </CardTitle>
                  <CardDescription>Enter credentials to access the floor.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="h-9 w-full bg-input/40 rounded-sm border border-primary/20 flex items-center px-3">
                      <span className="text-sm text-muted-foreground font-mono">user@tradesphere.io</span>
                    </div>
                  </div>
                  <Button className="w-full group/btn rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">
                    Access Terminal
                    <ChevronRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </>}

            {step.demo === "verify" && <>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <Lock className="w-4 h-4 text-primary" />
                    Liquidity Status
                  </CardTitle>
                  <CardDescription>Processing capital allocation...</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-mono">
                      <span className="text-muted-foreground">Deposit Progress</span>
                      <span className="text-primary font-bold">85%</span>
                    </div>
                    <Progress value={85} className="h-2 rounded-sm bg-muted" />
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-sm bg-primary/10 border border-primary/20">
                    <CircleCheck className="w-5 h-5 text-primary" />
                    <span className="text-sm font-bold text-foreground tracking-wide">Verified Liquidity</span>
                  </div>
                </CardContent>
              </>}

            {step.demo === "transact" && <>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <Globe className="w-4 h-4 text-primary" />
                    Trading Equity
                  </CardTitle>
                  <CardDescription>Instant liquidation active.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-sm bg-primary/10 border border-primary/20 flex flex-col gap-1">
                    <span className="text-xs text-primary font-bold uppercase tracking-widest">Liquid Funds</span>
                    <span className="text-3xl font-bold text-foreground font-mono">$124,500.00</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="default" className="flex-1 rounded-sm bg-primary text-primary-foreground" size="sm">
                      Trade
                    </Button>
                    <Button variant="outline" className="flex-1 rounded-sm border-primary/20 hover:bg-primary/10" size="sm">
                      Withdraw
                    </Button>
                  </div>
                </CardContent>
              </>}
          </Card>
        </div>
      </div>
    </div>;
}
export default function StepByStepOnboarding() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="capital-flow-system" className="bg-background py-24 md:py-32 relative overflow-hidden dark">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-secondary/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div ref={headerRef} className={`text-center max-w-2xl mx-auto mb-20 md:mb-32 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-sm font-black tracking-[0.3em] text-primary uppercase mb-4">
            Capital Flow System
          </h2>
          <h3 className="text-4xl md:text-6xl font-black text-foreground mb-6 tracking-tighter">
            Precision Liquidity & Execution
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Navigate the global markets with TradeSphere's institutional-grade infrastructure. Register, fund, and execute with sub-millisecond precision.
          </p>
        </div>

        <div className="relative">
          {steps.map((step, index) => <StepCard key={step.id} step={step} index={index} />)}
        </div>
      </div>
    </section>;
}